import { cx } from "@emotion/css";
import { MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import { useAuthContext } from "@/modules/auth/hooks/useAuthContext";
import { Loader } from "@/modules/theme/components/Loader/Loader";
import { PointsLabel } from "@/modules/theme/components/PointsLabel";
import { StarsRating } from "@/modules/theme/components/Stars/StarsRating";
import { Typography } from "@/modules/theme/components/Typography/Typography";
import { useTheme } from "@/modules/theme/hooks/useTheme";

import { Comment } from "../Comment/Comment";
import { RateInput } from "../RateInput/RateInput";

import { createRateSubtaskStyles } from "./createRateSubtaskStyles";
import { useRateSubtaskMutation } from "./useRateSubtaskQuery";

export type RateSubtaskProps = {
  id: string;
  name: string;
  pointsNumber: number;
  deadline?: string | null;
  comment: string | null;
  isOpen: boolean;
  rating: number;
  onItemClick: () => void;
};

const translationNamespace = "pages/DashboardPage";

export const RateSubtask = ({
  name,
  pointsNumber,
  deadline,
  comment,
  isOpen,
  rating,
  id,
  onItemClick,
}: RateSubtaskProps) => {
  const theme = useTheme();
  const classes = createRateSubtaskStyles(theme);
  const { t } = useTranslation(translationNamespace);
  const rateSubtaskMutation = useRateSubtaskMutation(onItemClick);
  const { userCompanyId } = useAuthContext();

  const handleOnRatingChange = (e: MouseEvent<HTMLElement>, rating: number) => {
    e.stopPropagation();
    if (userCompanyId) {
      rateSubtaskMutation.mutate({
        rating,
        companyId: userCompanyId,
        subtaskId: id,
      });
    }
  };

  return (
    // you cannot nest button inside button
    <div
      className={cx(classes.root, { [classes.rated]: !isOpen && rating > 0 })}
      onClick={onItemClick}
      role="button"
    >
      {rateSubtaskMutation.status === "loading" ? (
        <div className={classes.loaderWrapper}>
          <Loader size={30} variant="primary" />
        </div>
      ) : (
        <>
          <div className={classes.headerWrapper}>
            <p className={classes.header}>{name}</p>
            <PointsLabel points={pointsNumber} className={classes.points} />
          </div>
          <div className={classes.personWrapper}>
            {deadline && (
              <Typography variant="contentSmall">
                {t("Deadline")}:{" "}
                <span className={classes.deadline}>{deadline}</span>
              </Typography>
            )}
          </div>
          {isOpen ? (
            <>
              {comment && <Comment>{comment}</Comment>}
              <div className={classes.ratingWrapper}>
                <p className={classes.ratingLabel}>{t("Your rate")}:</p>
                <RateInput
                  onChange={handleOnRatingChange}
                  className={classes.rateInput}
                  currentRating={rating}
                />
              </div>
            </>
          ) : (
            <div className={classes.rating}>
              <span>{t("Rate")}:</span>
              <StarsRating value={rating} size={18} className={classes.stars} />
            </div>
          )}
        </>
      )}
    </div>
  );
};
